/**
 * SSOT for Luster's 4-corner scoring architecture + platform features.
 *
 * 3 scoring categories (Task Torque, Property Pulse, Signal) compose into
 * 1 composite (Luster Amplitude). Each category owns sub-dimensions
 * (configurable weights in Settings). 10 platform features feed data
 * into those categories.
 *
 * Every surface — ToolGrid, Dashboard manifold, TierFeatures, CortexFeed,
 * ScoringConfigSection, Topica specimens — imports from here. Do not
 * duplicate category names, icons, colors, or explainers elsewhere.
 *
 * Linear: IF-437 (Scoring Lego Blocks — modular config)
 */

import {
  BarChart3,
  Bell,
  Calendar,
  ClipboardList,
  FileText,
  FolderOpen,
  HeartPulse,
  Layers,
  Radio,
  Shield,
  Sparkles,
  Star,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import type { GlitchColorBase } from "./glitch"
import type { GlitchColor } from "./featureColors"
import type { ScoringDimensionId } from "./scoringConfig"

// ─── Scoring Categories (4 corners) ──────────────────────────────────────────

export type ScoringCategoryKey = "taskTorque" | "propertyPulse" | "signal" | "lusterAmplitude"

export interface SubDimension {
  id: ScoringDimensionId
  label: string
  weight: number
  dataSource: string
}

export interface ScoringCategory {
  key: ScoringCategoryKey
  word: string
  shortLabel: string
  icon: LucideIcon
  color: GlitchColor
  colorBase: GlitchColorBase
  explainer: string
  chartHex: string
  composite: boolean
  subDimensions: SubDimension[]
  tailwind: {
    text: string
    pill: string
    border: string
    headerBg: string
    headerTxt: string
    numTxt: string
  }
}

export const SCORING_CATEGORIES: ScoringCategory[] = [
  {
    key: "taskTorque",
    word: "Task Torque",
    shortLabel: "Torque",
    icon: ClipboardList,
    color: "teal",
    colorBase: "teal",
    explainer: "Did the work happen and was it done right? Checklist completion, protocol adherence, time efficiency.",
    chartHex: "rgb(45 212 191)",
    composite: false,
    subDimensions: [
      { id: "activity_completion", label: "Activity Completion", weight: 30, dataSource: "activity_reports.checklist" },
      { id: "protocol_adherence", label: "Protocol Adherence", weight: 10, dataSource: "activity_reports vs scheduled_tasks" },
      { id: "time_variance", label: "Time Variance", weight: 5, dataSource: "activity_reports.duration" },
    ],
    tailwind: {
      text: "text-teal-400",
      pill: "border-teal-400/20 bg-teal-950/40",
      border: "border-teal-400/20",
      headerBg: "bg-teal-500/10",
      headerTxt: "text-teal-400/90",
      numTxt: "text-teal-400",
    },
  },
  {
    key: "propertyPulse",
    word: "Property Pulse",
    shortLabel: "Pulse",
    icon: HeartPulse,
    color: "rose",
    colorBase: "red",
    explainer: "Is the property healthy and supplied? AI-derived facility condition plus supply inventory levels.",
    chartHex: "rgb(251 113 133)",
    composite: false,
    subDimensions: [
      { id: "facility_pulse", label: "Facility Health", weight: 20, dataSource: "gemini_analysis.facility_pulse" },
      { id: "supply_synapse", label: "Supply Levels", weight: 20, dataSource: "supply_snap_estimate + gemini" },
    ],
    tailwind: {
      text: "text-rose-400",
      pill: "border-rose-400/20 bg-rose-950/40",
      border: "border-rose-400/20",
      headerBg: "bg-rose-500/10",
      headerTxt: "text-rose-400/90",
      numTxt: "text-rose-400",
    },
  },
  {
    key: "signal",
    word: "Signal Fidelity",
    shortLabel: "Signal",
    icon: Radio,
    color: "amber",
    colorBase: "amber",
    explainer: "Is the operation running clean? Incident-free days, anomaly trace, operational stability.",
    chartHex: "rgb(251 191 36)",
    composite: false,
    subDimensions: [
      { id: "signal_stability", label: "Signal Stability", weight: 15, dataSource: "trace_entries (incident, anomaly)" },
    ],
    tailwind: {
      text: "text-amber-400",
      pill: "border-amber-400/20 bg-amber-950/40",
      border: "border-amber-400/20",
      headerBg: "bg-amber-500/10",
      headerTxt: "text-amber-400/90",
      numTxt: "text-amber-400",
    },
  },
  {
    key: "lusterAmplitude",
    word: "Luster Amplitude",
    shortLabel: "Amplitude",
    icon: BarChart3,
    color: "violet",
    colorBase: "violet",
    explainer: "Weighted composite of Task Torque + Property Pulse + Signal. The hero number.",
    chartHex: "rgb(124 58 237)",
    composite: true,
    subDimensions: [],
    tailwind: {
      text: "text-violet-400",
      pill: "border-violet-400/20 bg-violet-950/40",
      border: "border-violet-400/20",
      headerBg: "bg-violet-500/10",
      headerTxt: "text-violet-400/90",
      numTxt: "text-violet-400",
    },
  },
]

// ─── Platform Features ───────────────────────────────────────────────────────

export interface PlatformFeature {
  key: string
  name: string
  icon: LucideIcon
  category: ScoringCategoryKey
  explainer: string
  feeds: ScoringDimensionId[]
}

export const PLATFORM_FEATURES: PlatformFeature[] = [
  { key: "serviceHistory",     name: "Service History",     icon: ClipboardList, category: "taskTorque",      explainer: "Complete activity log — every service, every inspection, every note.",  feeds: ["activity_completion", "protocol_adherence"] },
  { key: "predictiveSchedule", name: "Predictive Schedule", icon: Calendar,      category: "taskTorque",      explainer: "AI-generated shift schedules that adapt to seasonality and demand.",     feeds: ["time_variance", "protocol_adherence"] },
  { key: "multiSiteView",      name: "Multi-Site View",     icon: Layers,        category: "taskTorque",      explainer: "Single-pane view across every property in your portfolio.",              feeds: ["activity_completion"] },
  { key: "reportIntelligence", name: "Report Intelligence", icon: FileText,      category: "propertyPulse",   explainer: "AI-scored activity reports with photo evidence and anomaly detection.", feeds: ["facility_pulse"] },
  { key: "documentVault",      name: "Document Vault",      icon: FolderOpen,    category: "propertyPulse",   explainer: "Centralized document storage with category filters and audit trail.",    feeds: ["supply_synapse"] },
  { key: "incidentTrace",      name: "Incident Trace",      icon: Radio,         category: "signal",          explainer: "Full audit trail for every incident — from report to resolution.",      feeds: ["signal_stability"] },
  { key: "notifications",      name: "Notifications",       icon: Bell,          category: "signal",          explainer: "Real-time signal feed — push, email, and in-app.",                      feeds: ["signal_stability"] },
  { key: "operatorScores",     name: "Operator Scores",     icon: Star,          category: "lusterAmplitude", explainer: "Per-operator performance scoring across all assigned properties.",       feeds: [] },
  { key: "cortexAI",           name: "Cortex AI",           icon: Sparkles,      category: "lusterAmplitude", explainer: "Conversational AI trained on your properties, reports, and history.",    feeds: [] },
  { key: "roleAccess",         name: "Role Access",         icon: Shield,        category: "lusterAmplitude", explainer: "Granular permissions — creator, admin, client, operator.",               feeds: [] },
]

// ─── Canonical helpers ───────────────────────────────────────────────────────

export function categoryByKey(k: ScoringCategoryKey): ScoringCategory {
  return SCORING_CATEGORIES.find(c => c.key === k)!
}

export function categoryByWord(w: string): ScoringCategory | undefined {
  return SCORING_CATEGORIES.find(c => c.word === w)
}

export function featuresByCategory(k: ScoringCategoryKey): PlatformFeature[] {
  return PLATFORM_FEATURES.filter(f => f.category === k)
}

/** Look up which category owns a given scoring dimension. */
export function categoryForDimension(dimId: ScoringDimensionId): ScoringCategory | undefined {
  return SCORING_CATEGORIES.find(c => c.subDimensions.some(d => d.id === dimId))
}

/** Derive dimension colors from the parent category (for ScoringConfigSection). */
export function dimensionColor(dimId: ScoringDimensionId): {
  bar: string; text: string; accent: string; glow: string
} {
  const cat = categoryForDimension(dimId)
  if (!cat) return { bar: "bg-white/30", text: "text-white/40", accent: "border-white/10", glow: "" }
  const base = cat.key === "taskTorque" ? "teal"
    : cat.key === "propertyPulse" ? "rose"
    : cat.key === "signal" ? "amber"
    : "violet"
  return {
    bar: `bg-${base}-500/70`,
    text: `text-${base}-400`,
    accent: `border-${base}-400/25`,
    glow: `shadow-${base}-500/20`,
  }
}

/** Derive dimension label and metadata from the parent category's sub-dimensions. */
export function dimensionMeta(dimId: ScoringDimensionId): {
  label: string; description: string; dataSource: string
} | undefined {
  for (const cat of SCORING_CATEGORIES) {
    const sub = cat.subDimensions.find(d => d.id === dimId)
    if (sub) {
      return {
        label: sub.label,
        description: `${cat.word} sub-dimension: ${sub.label.toLowerCase()}.`,
        dataSource: sub.dataSource,
      }
    }
  }
  return undefined
}

// ─── Backward compatibility (old 4-tool model) ──────────────────────────────
// Consumers that used facilityPulse / supplySynapse as top-level tools now
// resolve to propertyPulse. Migrate call sites to the new keys, then remove.

const LEGACY_KEY_MAP: Record<string, ScoringCategoryKey> = {
  facilityPulse: "propertyPulse",
  supplySynapse: "propertyPulse",
}

const LEGACY_WORD_MAP: Record<string, string> = {
  "Facility Pulse": "Property Pulse",
  "Supply Synapse": "Property Pulse",
}

/** @deprecated Use ScoringCategoryKey */
export type ProductToolKey = ScoringCategoryKey | "facilityPulse" | "supplySynapse"

/** @deprecated Use ScoringCategory */
export type ProductTool = ScoringCategory

/** @deprecated Use SCORING_CATEGORIES */
export const PRODUCT_TOOLS = SCORING_CATEGORIES

/** @deprecated Use categoryByKey — resolves legacy keys to new categories */
export function toolByKey(k: ProductToolKey | string): ScoringCategory {
  const resolved = LEGACY_KEY_MAP[k] ?? k
  return SCORING_CATEGORIES.find(c => c.key === resolved) ?? SCORING_CATEGORIES[0]
}

/** @deprecated Use categoryByWord — resolves legacy words to new categories */
export function toolByWord(w: string): ScoringCategory | undefined {
  const resolved = LEGACY_WORD_MAP[w] ?? w
  return SCORING_CATEGORIES.find(c => c.word === resolved)
}
